import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router'
import {
	BaseTandemAccountModel,
	TandemAccount,
} from '../../api/clients/tandem/DTOs'
import TandemAccountsClient from '../../api/clients/tandem/TandemAccountsClient'
import { FullscreenSpinner } from '../../components/feedback/circular'
import { toastService } from '../../services/notifications/ToastService'
import { TandemAccountSettingsSection } from './TandemAccountSettingsSection'
import { TandemFieldsSection } from './TandemFieldsSection'

export const TandemAccountPage = observer(() => {
	const params = useParams<{ accountId: string }>()
	const accountId = parseInt(params.accountId)
	if (isNaN(accountId)) throw new Error('invalid Tandem account id')

	const localStore = useLocalObservable(() => ({
		account: undefined as TandemAccount | undefined,
		existingAccountNames: [] as string[],
		selectedTabIndex: 0,
	}))

	const handleTabIndexChanged = useCallback(
		action((_event: React.SyntheticEvent, newValue: number) => {
			localStore.selectedTabIndex = newValue
		}),
		[],
	)

	useEffect(() => {
		fetchAccounts()
	}, [])

	const accountsClient = new TandemAccountsClient()

	const fetchAccounts = async () => {
		try {
			const { data: accounts } = await accountsClient.getAccounts()

			const account = accounts.find((v) => v.id === accountId)
			if (account === undefined) {
				toastService.displayToast({
					message: 'Failed to fetch account',
					area: 'global',
					severity: 'error',
				})
				return
			}

			runInAction(() => {
				localStore.existingAccountNames = accounts.map((v) => v.name)
				localStore.account = account
			})
		} catch (e) {
			console.error(e)

			toastService.displayToast({
				message: 'Failed to fetch accounts',
				area: 'global',
				severity: 'error',
			})
		}
	}

	const handleUpdateAccount = async (newModel: BaseTandemAccountModel) => {
		try {
			const { data: updatedAccount } = await accountsClient.updateAccount(
				accountId,
				newModel,
			)

			runInAction(() => {
				localStore.account = updatedAccount
			})

			toastService.displayToast({
				message: 'Account updated successfully',
				area: 'global',
				severity: 'success',
			})
		} catch (e) {
			console.error(e)

			toastService.displayToast({
				message: 'Failed to update account',
				area: 'global',
				severity: 'error',
			})
		}
	}

	const tabLabels = ['Configure', 'Fields']

	const tabSelector = () => {
		if (localStore.account === undefined) return <FullscreenSpinner />

		switch (localStore.selectedTabIndex) {
			case 0:
				return (
					<TandemAccountSettingsSection
						account={localStore.account}
						existingAccountNames={localStore.existingAccountNames}
						onUpdateAccount={handleUpdateAccount}
						tabLabels={tabLabels}
						onTabChanged={handleTabIndexChanged}
					/>
				)
			case 1:
				return (
					<TandemFieldsSection
						accountId={accountId}
						tabLabels={tabLabels}
						onTabChanged={handleTabIndexChanged}
					/>
				)
		}
	}

	return <>{tabSelector()}</>
})
